import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { LabelOption } from '@/api/api';
import { LabelingStatus, ListFolderContentsResponse } from '@/api/generatedApi';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from './useFetchFolderContents';

export const FOLDER_COLUMN_VISIBILITY_QUERY_KEY = 'folders.columnVisibility';

export type ColumnVisibilityField = 'descriptions' | 'label';

const COLUMN_VISIBILITY_INVALIDATION_DEBOUNCE_MS = 500;
let columnVisibilityInvalidationTimer: ReturnType<typeof setTimeout> | undefined;

const responseHasLabel = (response: AxiosResponse<ListFolderContentsResponse> | undefined): boolean => {
  if (!response) return false;
  return response.data.contents.some(
    item =>
      item.contentType === 'asset' &&
      (item.label?.status === LabelingStatus.Pending || item.label?.summary !== undefined)
  );
};

const hasAnyLabelInFolderCache = (queryClient: ReturnType<typeof useQueryClient>): boolean => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
  return queries.some(query =>
    responseHasLabel(queryClient.getQueryData<AxiosResponse<ListFolderContentsResponse>>(query.queryKey))
  );
};

export const useFolderColumnVisibility = ({ folderId, enabled = true }: { folderId?: string; enabled?: boolean }) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const { isTaxonomyEnabled } = useFeatureFlags();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: [FOLDER_COLUMN_VISIBILITY_QUERY_KEY, { folderId, tenantId }],
    queryFn: () => {
      return folderId
        ? api.tenants.getFolderColumnVisibility(tenantId, folderId)
        : api.tenants.getRootColumnVisibility(tenantId);
    },
    enabled: enabled && !!tenantId,
  });

  const metadataFieldNames = data?.data?.metadataFieldNames ?? [];
  const hasDescriptions = data?.data?.hasDescriptions ?? false;
  const labelOptions = (isTaxonomyEnabled ? (data?.data?.labels ?? []) : []) as LabelOption[];

  const [hasLabelInListCache, setHasLabelInListCache] = useState(() => hasAnyLabelInFolderCache(queryClient));

  useEffect(() => {
    if (!isTaxonomyEnabled) return;
    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      const eventKey = event.query.queryKey;
      if (!Array.isArray(eventKey) || eventKey[0] !== LIST_FOLDER_CONTENTS_QUERY_KEY) return;
      const eventQueryHasLabel = responseHasLabel(
        queryClient.getQueryData<AxiosResponse<ListFolderContentsResponse>>(eventKey)
      );
      setHasLabelInListCache(previous => {
        if (eventQueryHasLabel) return true;
        if (!previous) return false;
        return hasAnyLabelInFolderCache(queryClient);
      });
    });
    setHasLabelInListCache(hasAnyLabelInFolderCache(queryClient));
    return unsubscribe;
  }, [isTaxonomyEnabled, queryClient]);

  const hasLabels = isTaxonomyEnabled && (labelOptions.length > 0 || hasLabelInListCache);

  const ensureFresh = useCallback(
    (column?: ColumnVisibilityField) => {
      const visibilityByColumn: Record<ColumnVisibilityField, boolean> = {
        descriptions: hasDescriptions,
        label: hasLabels,
      };
      const alreadyVisible = column ? visibilityByColumn[column] : hasDescriptions && hasLabels;
      if (alreadyVisible) return;

      if (columnVisibilityInvalidationTimer) {
        clearTimeout(columnVisibilityInvalidationTimer);
      }
      columnVisibilityInvalidationTimer = setTimeout(() => {
        columnVisibilityInvalidationTimer = undefined;
        queryClient.invalidateQueries({ queryKey: [FOLDER_COLUMN_VISIBILITY_QUERY_KEY], exact: false });
      }, COLUMN_VISIBILITY_INVALIDATION_DEBOUNCE_MS);
    },
    [hasDescriptions, hasLabels, queryClient]
  );

  return {
    hasDescriptions,
    hasLabels,
    labelOptions,
    metadataFieldNames,
    isLoading,
    error,
    ensureFresh,
  };
};
